/**
 * @file macro.ts
 * @description GCOME_AP 宏按键信息区读写与解析。
 *
 * 协议细节（参见 docs/果来微科技_磁轴键盘AP通讯协议.md 4.8 与 README 4.8）：
 * - 命令：0x0c GetMacro / 0x0d SetMacro，2K 连续地址空间（不分板载，共享数据区）。
 * - 空间结构：
 *   - 宏指针区：前 64 字节，每个宏 2 字节指针，指向该宏动作的数据地址
 *     （绝对地址，第一个可用数据位置即 68）；指针值 0 表示该宏未定义。
 *   - 保留 4 字节（64-67，抓包发现的规则）。
 *   - 宏数据区：从 68 起。
 * - 每个宏动作 4 字节：
 *   - 字节 0..1：动作延时 Delay_L/Delay_H（小端）
 *   - 字节 2：类型字节，低 6 bit 为 Type（1=hotkey 2=key 3=mouse_key 4=mouse），
 *     Bit6 为 make/break（1=make 0=break），Bit7 为结束标记（1=end）
 *   - 字节 3：Code
 * - 32 个宏共享 2K 空间，由上位机管理分配；本实现采用：
 *   - 已有宏复用原数据位置（长度足够时）
 *   - 否则在数据区按 first-fit 找空闲间隙
 * - GCOME_AP 硬件无宏名字存储区、播放方式存于矩阵键 0x70 的 Code1（Outtime）。
 *   为兼容 SDK 通用宏语义（name/loop），宏元数据持久化在 APP 自行管理的数据区
 *   （0xf1/0xf2，绝对偏移，参见 docs/app-define-region.md）：
 *   - 宏名字表：0x0080 起固定 32 × 16 = 512 字节，UTF-8 空终止，
 *     每宏 16 字节（16 个 ASCII 字符，与 585 设备宏名称规范一致）
 *   - 播放方式表：0x0060 起固定 32 字节，每宏 1 字节，0xFF=未配置
 *   设备初始化（恢复出厂）时 App Define 区被清空（SDK 独占），无需避让外部工具数据。
 *   修改某宏的播放方式时，同步扫描所有板载 4 层矩阵中引用该宏（0x70 Code0）的键位，
 *   将其 Code1（Outtime）一并更新，保证宏级与键位级播放方式一致。
 */

import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import type { BaseDevice } from '../../../runtime/BaseDevice'
import type { Action, Macro } from '../../../types/macro'
import { extractPayloadString, encodeStringToBytes } from '../../../utils/StringUtils'
import { GCOME_AP_CMD, GCOME_AP_REPORT } from '../protocols/cmd'
import { readInfoBlock, writeInfoBlock } from './advanced-keys'

/** 宏最大数量。 */
export const MACRO_COUNT = 32

/** 单个宏动作的字节数。 */
export const MACRO_ACTION_SIZE = 4

/** 宏信息区总大小（2K 连续空间）。 */
export const MACRO_INFO_SIZE = 1024 * 2

/** 宏指针区固定大小（32 宏 × 2 字节）。 */
export const MACRO_POINTER_AREA_SIZE = 32 * 2

/** 指针区之后固定的空 4 字节（抓包发现规则）。 */
export const MACRO_RESERVED_SIZE = 4

/** 宏数据区起始偏移。 */
export const MACRO_DATA_BASE = MACRO_POINTER_AREA_SIZE + MACRO_RESERVED_SIZE

/** 宏名字最大字节数（16 字节 = 16 个 ASCII 字符，与 585 设备宏名称规范一致；UTF-8 编码下约 5 个汉字）。 */
export const MACRO_NAME_SIZE = 16

/** App Define 区：宏播放方式固定表偏移（32 字节，0xFF=未配置）。登记见 docs/app-define-region.md。 */
export const MACRO_PLAY_MODE_TABLE_OFFSET = 0x0060

/**
 * App Define 区：宏名字表偏移（0x0080 起固定 32 × 16 = 512 字节，UTF-8 空终止）。
 * 名字表与播放方式表（0x0060）同属宏元数据区，SDK 独占 App Define（初始化时清空）；
 * 宏名对全部板载一致（SDK 仅读写当前板载区）。登记见 docs/app-define-region.md。
 */
export const MACRO_NAME_TABLE_APP_OFFSET = 0x0080

/** 按键矩阵区总大小（4 层 × 512 字节）。 */
const KEY_MATRIX_TOTAL_SIZE = 512 * 4

/** 每层字节数。 */
const KEY_LAYER_SIZE = 512

/** 每键字节数。 */
const KEY_ITEM_SIZE = 3

/** 每层按键数。 */
const KEY_PER_LAYER = 128

/** 类型字节：动作类型掩码（低 6 bit）。 */
const ACTION_TYPE_MASK = 0x3f

/** 类型字节：make/break 位。 */
const ACTION_MAKE_BIT = 0x40

/** 类型字节：结束标记位。 */
const ACTION_END_BIT = 0x80

// ==================== 基础读写 ====================

/** 读取宏信息区完整原始字节（2048 字节，连续地址，不分板载）。 */
export function readMacroBlock(device: BaseDevice): Promise<Uint8Array> {
  return readInfoBlock(device, GCOME_AP_CMD.getMacro, MACRO_INFO_SIZE)
}

/** 写入宏信息区完整原始字节（分帧下发）。 */
export function writeMacroBlock(device: BaseDevice, data: Uint8Array): Promise<void> {
  return writeInfoBlock(device, GCOME_AP_CMD.setMacro, data)
}

// ==================== 动作编码/解析 ====================

/** 编码单个动作（不含结束标记）。 */
function encodeMacroAction(action: Action): number[] {
  const delay = Math.max(0, Math.min(0xffff, Math.round(action.time) || 0))
  const rawType = action.rawType ?? 2
  let typeByte = rawType & ACTION_TYPE_MASK
  if (action.type === 1) typeByte |= ACTION_MAKE_BIT
  return [delay & 0xff, delay >> 8, typeByte, action.key & 0xff]
}

/** 编码动作列表，末尾动作加结束标记。 */
export function encodeMacroActions(actions: Action[]): number[] {
  if (actions.length === 0) return []
  const bytes: number[] = []
  actions.forEach((action, i) => {
    const encoded = encodeMacroAction(action)
    if (i === actions.length - 1) encoded[2] |= ACTION_END_BIT
    bytes.push(...encoded)
  })
  return bytes
}

/**
 * 从数据区偏移开始解析动作序列。
 * 遇到结束标记（Bit7=1）或全零动作（防御，视为隐式结束）即停止。
 */
function parseMacroActions(block: Uint8Array, dataOffset: number): Action[] {
  const actions: Action[] = []
  let offset = dataOffset
  while (offset + MACRO_ACTION_SIZE <= block.length) {
    const delay = block[offset] | (block[offset + 1] << 8)
    const typeByte = block[offset + 2]
    const code = block[offset + 3]
    if (typeByte === 0 && delay === 0 && code === 0) break
    const isEnd = (typeByte & ACTION_END_BIT) !== 0
    const isMake = (typeByte & ACTION_MAKE_BIT) !== 0
    actions.push({
      time: delay,
      type: isMake ? 1 : 0,
      key: code,
      rawType: typeByte & ACTION_TYPE_MASK,
    })
    offset += MACRO_ACTION_SIZE
    if (isEnd) break
  }
  return actions
}

/** 测量数据区某偏移处宏动作序列占用的字节数（不含结束标记位自身）。 */
function measureMacroActionSize(block: Uint8Array, dataOffset: number): number {
  let offset = dataOffset
  let size = 0
  while (offset + MACRO_ACTION_SIZE <= block.length) {
    const typeByte = block[offset + 2]
    const delay = block[offset] | (block[offset + 1] << 8)
    const code = block[offset + 3]
    if (typeByte === 0 && delay === 0 && code === 0) break
    size += MACRO_ACTION_SIZE
    offset += MACRO_ACTION_SIZE
    if ((typeByte & ACTION_END_BIT) !== 0) break
  }
  return size
}

// ==================== 宏列表解析 ====================

/**
 * 底层工具：解析宏信息区，返回所有已定义（指针非 0）的宏。
 * 仅解析动作区；名字/播放方式（loop）由上层按 App Define 宏元数据表附加。
 * 注意：`getMacroList` 为轻量宏名字列表，不直接使用本函数。
 */
export function parseGcomeApMacro(block: Uint8Array): Macro[] {
  const macros: Macro[] = []
  for (let i = 0; i < MACRO_COUNT; i++) {
    const ptr = block[i * 2] | (block[i * 2 + 1] << 8)
    if (ptr === 0) continue
    macros.push({
      name: `宏${i}`,
      index: i,
      package: parseMacroActions(block, ptr),
    })
  }
  return macros
}

/** 解析单个宏（索引越界或未定义时抛 INVALID_PARAM）。 */
export function parseGcomeApMacroByIndex(block: Uint8Array, index: number): Macro {
  if (!Number.isInteger(index) || index < 0 || index >= MACRO_COUNT) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `宏索引必须是 0-${MACRO_COUNT - 1}，当前为 ${index}。`,
    )
  }
  const ptr = block[index * 2] | (block[index * 2 + 1] << 8)
  if (ptr === 0) {
    throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `宏 ${index} 未定义。`)
  }
  return {
    name: `宏${index}`,
    index,
    package: parseMacroActions(block, ptr),
  }
}

// ==================== 空间分配 ====================

/**
 * 在宏数据区寻找空闲间隙（first-fit）。
 * @param block  宏信息区原始字节
 * @param length 需要容纳的字节数
 * @returns 数据区绝对偏移（≥ 68）；空间不足返回 -1
 */
export function findFreeMacroDataOffset(block: Uint8Array, length: number): number {
  const ranges: Array<[number, number]> = []
  for (let i = 0; i < MACRO_COUNT; i++) {
    const ptr = block[i * 2] | (block[i * 2 + 1] << 8)
    if (ptr === 0) continue
    if (ptr >= block.length) continue
    ranges.push([ptr, ptr + measureMacroActionSize(block, ptr)])
  }
  ranges.sort((a, b) => a[0] - b[0])

  let cursor = MACRO_DATA_BASE
  for (const [start, end] of ranges) {
    if (start - cursor >= length) return cursor
    if (end > cursor) cursor = end
  }
  if (block.length - cursor >= length) return cursor
  return -1
}

/**
 * 将宏写入信息区（读-改-写整块）：
 * - 空动作列表 → 清空宏（指针置 0）
 * - 已有宏且原位置长度足够 → 覆盖原位置
 * - 否则 first-fit 分配新位置
 */
export function encodeGcomeApMacroBlock(
  block: Uint8Array,
  macro: Macro,
  index: number,
): Uint8Array {
  if (!Number.isInteger(index) || index < 0 || index >= MACRO_COUNT) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `宏索引必须是 0-${MACRO_COUNT - 1}，当前为 ${index}。`,
    )
  }

  const actions = macro.package ?? []
  const bytes = encodeMacroActions(actions)

  const next = new Uint8Array(block)
  if (bytes.length === 0) {
    next[index * 2] = 0
    next[index * 2 + 1] = 0
    return next
  }

  let offset = -1
  const existingPtr = next[index * 2] | (next[index * 2 + 1] << 8)
  if (existingPtr !== 0) {
    if (existingPtr + bytes.length <= next.length) offset = existingPtr
  }
  if (offset < 0) offset = findFreeMacroDataOffset(next, bytes.length)
  if (offset < 0) {
    throw new DeviceError(
      DeviceErrorCode.MAX_LIMIT_REACHED,
      `宏数据区空间不足，无法写入宏 ${index}（需要 ${bytes.length} 字节）。`,
    )
  }

  next.set(bytes, offset)
  next[index * 2] = offset & 0xff
  next[index * 2 + 1] = offset >> 8
  return next
}

// ==================== App Define 宏元数据（名字 + 播放方式） ====================

/** App Define 命令接口。 */
interface GcomeApAppDefineDevice {
  sendRawApCommand(request: {
    command: number
    address?: number
    data?: number[] | Uint8Array
  }): Promise<{ response: { data: Uint8Array } }>
}

/** 从 App Define 区指定绝对偏移分帧读取原始字节。 */
async function readAppDefineBlock(
  device: BaseDevice,
  address: number,
  length: number,
): Promise<Uint8Array> {
  const gcome = device as unknown as GcomeApAppDefineDevice
  const chunks: Uint8Array[] = []
  const maxFrames = Math.ceil(length / GCOME_AP_REPORT.payloadSize)

  for (let i = 0; i < maxFrames; i++) {
    const frameLen = Math.min(GCOME_AP_REPORT.payloadSize, length - i * GCOME_AP_REPORT.payloadSize)
    const res = await gcome.sendRawApCommand({
      command: GCOME_AP_CMD.getAppDefine,
      address: address + i * GCOME_AP_REPORT.payloadSize,
      data: new Array(frameLen).fill(0),
    })
    const chunk = res.response.data
    chunks.push(chunk)
    if (chunk.length < frameLen) break
  }

  const result = new Uint8Array(length)
  let offset = 0
  for (const chunk of chunks) {
    if (offset >= length) break
    const len = Math.min(chunk.length, length - offset)
    result.set(chunk.slice(0, len), offset)
    offset += len
  }
  return result
}

/** 向 App Define 区指定绝对偏移写入原始字节（单帧，≤56 字节）。 */
async function writeAppDefineAt(
  device: BaseDevice,
  address: number,
  data: number[],
): Promise<void> {
  const gcome = device as unknown as GcomeApAppDefineDevice
  await gcome.sendRawApCommand({
    command: GCOME_AP_CMD.setAppDefine,
    address,
    data,
  })
}

/**
 * 读取宏播放方式表（32 字节固定表）。
 * @returns 每宏 1 字节（0-255）；未配置（`0xFF`）按默认 `0`（按住持续播放）返回。
 */
export async function readMacroPlayModes(device: BaseDevice): Promise<number[]> {
  const data = await readAppDefineBlock(device, MACRO_PLAY_MODE_TABLE_OFFSET, MACRO_COUNT)
  return Array.from({ length: MACRO_COUNT }, (_, i) => (data[i] === 0xff ? 0 : (data[i] ?? 0)))
}

/** 写入单个宏的播放方式（0-255），`0xFF` 表示清除（未配置）。固定表按 index 写单字节，无需读-改-写。 */
export async function writeMacroPlayMode(
  device: BaseDevice,
  index: number,
  outtime: number,
): Promise<void> {
  await writeAppDefineAt(device, MACRO_PLAY_MODE_TABLE_OFFSET + index, [outtime & 0xff])
}

/**
 * 读取宏名字表（32 × 16 字节固定表，位于 App Define 区 0x0080 起，UTF-8 空终止）。
 * @returns 32 个宏名，未命名（0xFF/0x00 填充）返回空串。
 */
export async function readMacroNames(device: BaseDevice): Promise<string[]> {
  const table = await readAppDefineBlock(
    device,
    MACRO_NAME_TABLE_APP_OFFSET,
    MACRO_COUNT * MACRO_NAME_SIZE,
  )
  const names: string[] = []
  for (let i = 0; i < MACRO_COUNT; i++) {
    const slice = table.slice(i * MACRO_NAME_SIZE, (i + 1) * MACRO_NAME_SIZE)
    if (slice[0] === 0xff || slice[0] === 0) {
      names.push('')
    } else {
      names.push(extractPayloadString(slice))
    }
  }
  return names
}

/** 写入单个宏的名字（UTF-8，超出 MACRO_NAME_SIZE 字节截断，空串清除）。 */
export async function writeMacroName(
  device: BaseDevice,
  index: number,
  name: string,
): Promise<void> {
  const bytes = encodeStringToBytes(name, MACRO_NAME_SIZE)
  await writeAppDefineAt(
    device,
    MACRO_NAME_TABLE_APP_OFFSET + index * MACRO_NAME_SIZE,
    Array.from(bytes),
  )
}

// ==================== 播放方式键位同步 ====================

/**
 * 同步宏的播放方式到所有板载 4 层矩阵中引用该宏的键位：
 * 矩阵键 type=0x70 且 Code0=macroIndex 时，将 Code1（Outtime）改为 outtime。
 * @returns 同步的键位数量
 */
export async function syncMacroPlayModeToKeys(
  device: BaseDevice,
  macroIndex: number,
  outtime: number,
): Promise<number> {
  let total = 0
  const value = outtime & 0xff

  for (let profile = 0; profile < 4; profile++) {
    const block = await readInfoBlock(
      device,
      GCOME_AP_CMD.getUseKeyMatrix,
      KEY_MATRIX_TOTAL_SIZE,
      profile,
    )
    let hits = 0
    for (let layer = 0; layer < 4; layer++) {
      const layerBase = layer * KEY_LAYER_SIZE
      for (let key = 0; key < KEY_PER_LAYER; key++) {
        const off = layerBase + key * KEY_ITEM_SIZE
        if (block[off] === 0x70 && block[off + 1] === macroIndex) {
          block[off + 2] = value
          hits++
        }
      }
    }
    if (hits > 0) {
      await writeInfoBlock(device, GCOME_AP_CMD.setUseKeyMatrix, block, profile)
      total += hits
    }
  }
  return total
}
